import { useMemo, useState } from 'react';
import { Info } from '@phosphor-icons/react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalContent,
  Slider,
  SliderMark,
  SliderThumb,
  SliderTrack,
  SliderFilledTrack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { ForestAccountCalculator as Props } from '../../types';
import ButtonLink from '../ButtonLink';
import HtmlContent from '../HtmlContent';

interface State {
  loanAmount: number;
  loanTime: number;
  interest: number[];
}

const updateTotal = (
  amount: number,
  time: number,
  interest: number[],
): number => {
  const total = Math.round((amount * interest[time - 1]) / 100);
  if (total < 50) {
    return 50;
  }
  return total;
};

const ForestAccountCalculator = ({
  title,
  link,
  loanAmount: loanAmountFields,
  loanTime: loanTimeFields,
  instructions,
}: Props) => {
  const {
    min: loanAmountMin,
    max: loanAmountMax,
    step: loanAmountStep,
    title: loanAmountTitle,
  } = loanAmountFields;

  const {
    min: loanTimeMin,
    max: loanTimeMax,
    title: loanTimeTitle,
  } = loanTimeFields;

  const [loanMin, loanMax, loanstep, timeMin, timeMax] = [
    loanAmountMin,
    loanAmountMax,
    loanAmountStep,
    loanTimeMin,
    loanTimeMax,
  ].map(Number);

  const [{ loanAmount, loanTime, interest }, setState] = useState<State>({
    loanAmount: loanMin || 2500,
    loanTime: timeMin || 1,
    interest: [9, 5, 3.3, 2.8, 2.1, 1.7],
  });

  const total = useMemo(
    () => updateTotal(loanAmount, loanTime, interest),
    [loanAmount, loanTime, interest],
  );

  const onSliderChange = (name: string, val: number): void => {
    setState((s) => ({
      ...s,
      [name]: val,
    }));
  };

  const sliderLabelStyles = {
    top: '18px',
    left: '0!important',
  };
  const sliderMarkStyles = {
    transform: 'translateX(-50%)',
    mt: '-12',
    px: '2.5',
    py: '1.5',
    bg: 'white',
    fontWeight: 'bold',
    color: 'greenDarker',
    textStyle: 'sm',
  };

  const { isOpen, onClose, onOpen } = useDisclosure();

  return (
    <Box>
      <Flex
        gridGap="5"
        justifyContent="space-around"
        bg="greenLight"
        flexWrap="wrap"
        borderRadius="lg"
        px={[4, 6, 10]}
        py={[6, 10]}
      >
        <Heading flex="0 0 100%" as="h4" variant="h5" mb={7}>
          {title}
          <Button
            sx={{ '> svg': { color: 'greenSpruce' } }}
            variant="transparent"
            onClick={onOpen}
            ml={1}
          >
            <Info size="24" />
          </Button>
        </Heading>
        <Box flex="0 0 70%" px={5}>
          <Box pt={7} pb={14}>
            <Slider
              min={loanMin}
              max={loanMax}
              step={loanstep}
              defaultValue={loanMin}
              aria-label={loanAmountTitle}
              onChange={(val) => onSliderChange('loanAmount', val)}
            >
              <SliderMark mt={1} value={loanMin} {...sliderLabelStyles}>
                {loanAmountTitle}
              </SliderMark>
              <SliderMark value={loanAmount} {...sliderMarkStyles}>
                {loanAmount}€
              </SliderMark>
              <SliderTrack bg="greenSpruce">
                <SliderFilledTrack bg="greenBirch" />
              </SliderTrack>
              <SliderThumb bg="white" width="20px" height="20px" />
            </Slider>
          </Box>

          <Box py={7}>
            <Slider
              min={timeMin}
              max={timeMax}
              defaultValue={timeMin}
              aria-label={loanAmountTitle}
              onChange={(val) => onSliderChange('loanTime', val)}
            >
              <SliderMark mt={1} value={timeMin} {...sliderLabelStyles}>
                {loanTimeTitle}
              </SliderMark>
              <SliderMark value={loanTime} {...sliderMarkStyles}>
                {loanTime}v
              </SliderMark>
              <SliderTrack bg="greenSpruce">
                <SliderFilledTrack bg="greenBirch" />
              </SliderTrack>
              <SliderThumb bg="white" width="20px" height="20px" />
            </Slider>
          </Box>
        </Box>
        <Flex direction="column" justifyContent="space-around">
          {link && (
            <ButtonLink
              width="100%"
              link={link}
              size="xl"
              variant="solidGreen"
            />
          )}
          <Box
            px={8}
            py={5}
            textAlign="center"
            bg="gray2"
            width="160px"
            maxWidth="160px"
          >
            <Text
              whiteSpace="nowrap"
              fontSize="2em"
              fontWeight="bold"
              as="span"
            >
              {total}
              <Text textStyle="sm" as="span">
                €/kk
              </Text>
            </Text>
          </Box>
        </Flex>
      </Flex>

      {instructions && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent p="3">
            <ModalCloseButton onClick={onClose} />
            <ModalBody>
              <HtmlContent html={instructions} />
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default ForestAccountCalculator;
